import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { subscribeToNotifications } from '../services/notificationService';
import { formatDistance } from 'date-fns';
import ToastContainer from '../components/ui/ToastContainer';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'badge' | 'task' | 'overdue' | 'priority' | 'candidate' | 'appointment';
  title: string;
  message: string;
  duration?: number;
  timestamp: string;
  time?: string;
  priority?: 'high' | 'medium' | 'low';
  read?: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  showNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  notifyBadgeChange: (pageName: string, badgeType: string, oldValue: any, newValue: any) => void;
  notifyContentChange: (pageName: string, action: 'added' | 'deleted', itemType: string, itemName?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Keep the old export for backward compatibility
export const useNotification = useNotifications;

interface NotificationProviderProps {
  children: ReactNode;
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // State for toasts (pop-up notifications)
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  // Load notifications from localStorage or use sample data
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    try {
      const stored = localStorage.getItem('notifications');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading notifications from localStorage:', error);
    }
    
    // Sample notifications for development
    return [
      {
        id: '1',
        type: 'task',
        title: 'Task Reminder',
        message: 'Review Sarah Johnson\'s Documents is due tomorrow',
        timestamp: new Date().toISOString(),
        time: '2 hours ago',
        priority: 'high',
        read: false
      },
      {
        id: '2',
        type: 'candidate',
        title: 'New Candidate',
        message: 'James Omondi has updated his profile',
        timestamp: new Date().toISOString(),
        time: '1 day ago',
        priority: 'medium',
        read: false
      },
      {
        id: '3',
        type: 'appointment',
        title: 'Upcoming Interview',
        message: 'Interview with Jane Mwangi scheduled for tomorrow at 10:00 AM',
        timestamp: new Date().toISOString(),
        time: '3 hours ago',
        priority: 'high',
        read: true
      }
    ];
  });

  // Save notifications to localStorage whenever they change
  React.useEffect(() => {
    try {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    } catch (error) {
      console.error('Error saving notifications to localStorage:', error);
    }
  }, [notifications]);

  // Set up Firebase real-time subscription for live sync
  useEffect(() => {
    console.log('🔔 NotificationContext: Setting up Firebase real-time subscription...');
    
    let unsubscribe: (() => void) | null = null;
    
    const setupFirebaseSubscription = async () => {
      try {
        unsubscribe = subscribeToNotifications((firebaseNotifications) => {
          console.log('🔔 NotificationContext: Received', firebaseNotifications.length, 'notifications from Firebase');
          
          if (firebaseNotifications.length > 0) {
            // Convert Firebase notifications to match local interface
            const convertedNotifications: Notification[] = firebaseNotifications.map(firebaseNotif => ({
              id: firebaseNotif.id || '',
              type: firebaseNotif.type === 'task_overdue' ? 'overdue' : 
                    firebaseNotif.type === 'appointment_today' ? 'appointment' : 
                    firebaseNotif.type === 'candidate_stage_change' ? 'candidate' : 
                    'info',
              title: firebaseNotif.title,
              message: firebaseNotif.message,
              timestamp: firebaseNotif.createdAt ? new Date(firebaseNotif.createdAt.seconds * 1000).toISOString() : new Date().toISOString(),
              time: firebaseNotif.createdAt ? formatDistance(new Date(firebaseNotif.createdAt.seconds * 1000), new Date()) + ' ago' : 'just now',
              priority: 'medium',
              read: firebaseNotif.read || false
            }));
            setNotifications(convertedNotifications);
          }
          // If no Firebase data, keep existing localStorage data (sample data)
        });
      } catch (error) {
        console.error('🔔 NotificationContext: Firebase subscription error:', error);
        // Continue using localStorage data as fallback
      }
    };

    setupFirebaseSubscription();

    return () => {
      console.log('🔔 NotificationContext: Cleaning up Firebase subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Listen for cross-window/tab storage changes for localStorage sync
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'notifications' || event.key === null) {
        console.log('🔔 NotificationContext: Storage change detected, updating notifications');
        try {
          const stored = localStorage.getItem('notifications');
          if (stored) {
            const parsedNotifications = JSON.parse(stored);
            setNotifications(parsedNotifications);
          }
        } catch (error) {
          console.error('🔔 NotificationContext: Error loading notifications from storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const unreadCount = useMemo(() => {
    return notifications.filter(notification => !notification.read).length;
  }, [notifications]);

  const showNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date().toISOString(),
      time: 'Just now',
      read: false
    };

    setNotifications(prev => [newNotification, ...prev]);
    
    // Also show as toast for certain types
    if (['success', 'error', 'warning', 'info'].includes(notification.type)) {
      const toast: Toast = {
        id,
        type: notification.type as 'success' | 'error' | 'warning' | 'info',
        title: notification.title,
        message: notification.message,
        duration: notification.duration || 8000
      };
      setToasts(prev => [...prev, toast]);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  const notifyBadgeChange = useCallback((pageName: string, badgeType: string, oldValue: any, newValue: any) => {
    if (oldValue !== newValue) {
      const notification: Omit<Notification, 'id' | 'timestamp'> = {
        type: 'badge',
        title: `Badge Updated - ${pageName}`,
        message: `${badgeType} changed from "${oldValue}" to "${newValue}"`,
        priority: 'medium',
        read: false
      };
      showNotification(notification);
    }
  }, [showNotification]);

  const notifyContentChange = useCallback((pageName: string, action: 'added' | 'deleted', itemType: string, itemName?: string) => {
    const actionText = action === 'added' ? 'Added' : 'Deleted';
    const itemText = itemName ? `"${itemName}"` : `a ${itemType}`;
    
    const notification: Omit<Notification, 'id' | 'timestamp'> = {
      type: action === 'added' ? 'success' : 'warning',
      title: `${actionText} ${itemType} - ${pageName}`,
      message: `${actionText} ${itemText} on ${pageName}`,
      priority: action === 'deleted' ? 'high' : 'medium',
      read: false
    };
    showNotification(notification);
  }, [showNotification]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    showNotification,
    removeNotification,
    clearAllNotifications,
    markAsRead,
    markAllAsRead,
    notifyBadgeChange,
    notifyContentChange
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </NotificationContext.Provider>
  );
};